import React, { useState } from 'react';
import { Grid, IconButton, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import { Clear } from '@mui/icons-material';
import moment from 'moment';
import { useAuth } from '../../../session/InternalAuthProvider';
import { AccountData, HolidayCalendar } from '../../../api';
import { getTimeFormat } from '../../../common/getDateAndTimeFormat';
import SharedMobileTimePicker from '../../../common/ui/SharedMobileTimePicker';

interface MultiTimePickerProps {
  times: number[];
  onChange: (times: number[]) => void;
  minTime?: moment.Moment;
  isEditEvent: boolean;
}

export const MultiTimePicker = ({ times, onChange, minTime, isEditEvent }: MultiTimePickerProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);

  const [addingNewTime, setAddingNewTime] = useState(false);
  const [newTime, setNewTime] = useState(moment().startOf('day'));

  const handleDelete = (secs: number) => {
    onChange(times.filter((t) => t !== secs));
  };

  if (isEditEvent) {
    return (
      <SharedMobileTimePicker
        sx={{ width: account.holiday_calendar === HolidayCalendar.US ? '120px' : '110px' }}
        value={moment()
          .startOf('day')
          .add(times[0] ?? 32400, 'seconds')}
        minutesStep={15}
        {...(minTime && { minTime })}
        onChange={(newValue: moment.Moment | null) => {
          if (newValue) {
            onChange([newValue.hours() * 3600 + newValue.minutes() * 60]);
          }
        }}
      />
    );
  }

  return (
    <Grid display="flex" gap={1} flexWrap="wrap" alignItems="center">
      {[...times]
        .sort((a, b) => a - b)
        .map((secs) =>
          times.length === 1 ? (
            <Grid item key={secs}>
              <SharedMobileTimePicker
                sx={{ width: account.holiday_calendar === HolidayCalendar.US ? '120px' : '110px' }}
                value={moment().startOf('day').add(secs, 'seconds')}
                minutesStep={15}
                {...(minTime && { minTime })}
                onChange={(newValue: moment.Moment | null) => {
                  if (newValue) {
                    onChange([newValue.hours() * 3600 + newValue.minutes() * 60]);
                  }
                }}
              />
            </Grid>
          ) : (
            <Grid item key={secs}>
              <TextField
                disabled
                sx={{
                  width: account.holiday_calendar === HolidayCalendar.US ? '120px' : '110px',
                  '&& .MuiInputBase-root': { paddingInlineStart: '0px' },
                }}
                value={moment().startOf('day').add(secs, 'seconds').format(timeFormat)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => handleDelete(secs)} size="small">
                      <Clear />
                    </IconButton>
                  ),
                  sx: {
                    paddingInlineEnd: 0,
                    color: 'text.primary',
                    '& .MuiInputAdornment-positionEnd': { marginInlineStart: 0 },
                  },
                }}
              />
            </Grid>
          ),
        )}
      {!isEditEvent && (
        <Grid item>
          {addingNewTime ? (
            <SharedMobileTimePicker
              sx={{ width: '110px' }}
              value={newTime}
              minutesStep={15}
              {...(minTime && { minTime })}
              onChange={(newValue: moment.Moment | null) => setNewTime(newValue!)}
              onAccept={(newValue: moment.Moment | null) => {
                if (newValue) {
                  const secs = newValue.hours() * 3600 + newValue.minutes() * 60;
                  if (!times.includes(secs)) {
                    onChange([...times, secs].sort((a, b) => a - b));
                  }
                }
                setAddingNewTime(false);
                setNewTime(moment().startOf('day'));
              }}
            />
          ) : (
            <Button onClick={() => setAddingNewTime(true)} variant="outlined" color="inherit" sx={{ height: '44px' }}>
              <Add />
            </Button>
          )}
        </Grid>
      )}
    </Grid>
  );
};
